import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CardIconTitleDescription, RappiderCardWithIconTitleDescriptionListComponent } from '@rappider/rappider-components';
import { Page, ProjectModelWithRelations, WorkflowEventWithRelations, WorkflowStepFunctionWithRelations } from '@rappider/rappider-sdk';
import { orderBy } from 'lodash';
import { combineLatest, Subscription, withLatestFrom } from 'rxjs';

@Component({
  selector: 'rappider-manage-workflows',
  standalone: true,
  imports: [
    CommonModule,
    RappiderCardWithIconTitleDescriptionListComponent
  ],
  templateUrl: './manage-workflows.component.html',
  styleUrls: ['./manage-workflows.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageWorkflowsComponent implements OnInit, OnDestroy {

  subscriptions: Subscription[] = [];

  workflowStepFunctions: WorkflowStepFunctionWithRelations[] | undefined;
  workflowEvents: WorkflowEventWithRelations[] | undefined;

  starterWorkflowStepFunctions: WorkflowStepFunctionWithRelations[] = [];
  starterWorkflowEvents: WorkflowEventWithRelations[] = [];
  starterWorkflowEventsLoaded = false;
  starterWorkflowStepFunctionsLoaded = false;

  projectModels: ProjectModelWithRelations[] = [];

  uiManagementCards?: CardIconTitleDescription[] = [
    {
      id: 'create-workflow',
      title: 'Create New Workflow',
      key: 'create-workflow',
      iconName: 'fa-regular fa-square-plus',
      description: 'Start with a clean slate and design your workflow from scratch with full customization options',
    },
    {
      id: 'create-ai-workflow',
      title: 'Create Workflow with Assistant',
      key: 'create-ai-workflow',
      iconName: 'fa-solid fa-sparkles',
      description: 'Leverage AI Assistant to quickly generate a professional workflow based on your requirements and specifications',
    }
  ];

  starterWorkflowCards?: CardIconTitleDescription[] = [];

  starterWorkflowEventCards?: CardIconTitleDescription[] = [
    {
      id: 'starterWorkflowEventLoading',
      title: 'Workflows loading...',
      key: 'starterWorkflowEventLoading',
      iconName: 'fa-solid fa-circle-notch fa-spin',
      description: 'Workflows that starts with an event are loading...',
    }
  ];
  starterWorkflowStepFunctionCards?: CardIconTitleDescription[] = [
    {
      id: 'starterWorkflowStepFunctionLoading',
      title: 'Workflows loading...',
      key: 'starterWorkflowStepFunctionLoading',
      iconName: 'fa-solid fa-circle-notch fa-spin',
      description: 'Workflows that starts with a step function are loading...',
    }
  ];

  constructor(
    private store: Store<any>,
    private router: Router,
    private cd: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.subscribeToData();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }


  subscribeToData() {
    this.subscriptions = [
      this.subscribeToWorkflowStepFunctionsAndWorkflowEvents()
    ];
  }

  subscribeToWorkflowStepFunctionsAndWorkflowEvents() {
    return combineLatest([
      this.store.select(state => state.workflowStepFunction?.data),
      this.store.select(state => state.workflowEvent?.data)
    ]).subscribe((
      [workflowStepFunctions, workflowEvents]:
        [WorkflowStepFunctionWithRelations[] | undefined, WorkflowEventWithRelations[] | undefined]) => {
      this.workflowStepFunctions = workflowStepFunctions;
      this.workflowEvents = workflowEvents;
      console.log('workflowStepFunctions', this.workflowStepFunctions);
      console.log('workflowEvents', this.workflowEvents);

      this.calculateStarterWorkflowStepFunctions();
    });
  }

  /*  Step functions without a subscribedEvent are starter step functions */
  calculateStarterWorkflowStepFunctions() {
    if (!this.workflowStepFunctions) {
      return;
    }

    this.starterWorkflowStepFunctions = this.workflowStepFunctions?.filter(stepFunction =>
      !stepFunction.subscribedEvents || stepFunction.subscribedEvents.length === 0) ?? [];
    console.log('starterWorkflowStepFunctions', this.starterWorkflowStepFunctions);

    if (this.starterWorkflowStepFunctions.length) {
      this.mapStarterWorkflowStepFunctionsToCards();
    }

    this.starterWorkflowStepFunctionsLoaded = true;

    // after step functions are loaded, and mapped to cards, we can calculate the starter events
    this.calculateStarterWorkflowEvents();
  }
  /* Events - that are not published by a step function on success or failure - are starter events */
  calculateStarterWorkflowEvents() {
    if (!this.workflowEvents) {
      return;
    }

    // events that are published by a step function on failure
    const allPublishedEventOnFailureIds = this.workflowStepFunctions?.flatMap(stepFunction =>
      stepFunction.publishedEventsOnFailure ?? []).map(event => event.id) ?? [];

    // events that are published by a step function on success
    const allPublishedEventsOnSuccessIds = this.workflowStepFunctions?.flatMap(stepFunction =>
      stepFunction.publishedEventsOnSuccess ?? []).map(event => event.id) ?? [];

    // events that has a step function connected to them
    // const allSubscribedEventIds = this.workflowStepFunctions?.flatMap(stepFunction =>
    //   stepFunction.subscribedEvents ?? []).map(event => event.id) ?? [];

    this.starterWorkflowEvents = this.workflowEvents.filter(event =>
      // is not published by any step function on success or failure
      !allPublishedEventOnFailureIds.includes(event.id) &&
      !allPublishedEventsOnSuccessIds.includes(event.id));

    console.log('starterWorkflowEvents', this.starterWorkflowEvents);

    if (this.starterWorkflowEvents.length) {
      this.mapStarterWorkflowEventsToCards();
    }

    // combine the cards of step functions and events
    this.starterWorkflowCards = orderBy([
      ...this.starterWorkflowEventCards ?? [],
      ...this.starterWorkflowStepFunctionCards ?? []
    ], ['title'], ['asc']);

    // to display the loading spinner
    this.starterWorkflowEventsLoaded = true;


  }

  mapStarterWorkflowEventsToCards() {
    this.starterWorkflowEventCards = this.starterWorkflowEvents.map(event => ({
      id: event.id,
      title: (event.title || event.name) ? (event.title || event.name) : 'Event',
      key: event.name,
      tags: [{
        text: 'Event',
        iconName: 'fa-solid fa-bolt',
        //color: 'cyan'
      }],
      description: event.description ?? undefined,
    }));

    this.cd.markForCheck();
  }

  mapStarterWorkflowStepFunctionsToCards() {
    this.starterWorkflowStepFunctionCards = this.starterWorkflowStepFunctions.map(stepFunction => ({
      id: stepFunction.id,
      // USE  mode : "ai-function"
      title: this.capitalizeFirstChar(stepFunction.title || stepFunction.name) ?? 'Step Function',
      key: stepFunction.name,
      tags: stepFunction.mode ? this.mapStepFunctionModeToTag(stepFunction.mode) : [],
      description: stepFunction.description ?? undefined,
    }));

    this.cd.markForCheck();
  }

  mapStepFunctionModeToTag(mode: string | undefined) {
    if (!mode) {
      return [];
    }

    // "endpoint" | "custom-function-definition" | "ai-function"
    switch (mode) {
      case 'ai-function':
        return [{
          text: 'AI Call',
          iconName: 'fa-solid fa-robot',
          // color: 'purple'
        }];
      case 'custom-function-definition':
        return [{
          text: 'Custom Lambda Function',
          iconName: 'fa-solid fa-code',
          // color: 'blue'
        }];
      case 'endpoint':
        return [{
          text: 'HTTP Request',
          iconName: 'fa-solid fa-plug',
          // color: 'magenta'
        }];
      default:
        return [];
    }


  }

  capitalizeFirstChar(str: string): string {
    if (!str) {
      return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  onUiManagementCardCardClicked(card: any) {
    console.log('onUiManagementCardCardClicked ', card);
  }

  onUiPageCardCardClicked(card: any) {
    console.log('onUiPageCardCardClicked ', card);
    this.router.navigate(['/content-editor/', card.id]);
  }

  onEntityCardCardClicked(card: any) {
    console.log('onEntityCardCardClicked ', card);
    this.router.navigate(['/ai/entity-visualization/', card.id]);
  }
}
